import {
    deriveSkuAllocationChecks,
    PERCENT_TOLERANCE,
    roundPercentFraction,
} from "./skuAllocationDerivedChecks";

const SUBCATEGORY_TOTAL_TARGET = 1;

function roundMetric(value) {
    return Number(Number(value).toFixed(2));
}

function formatMovePercent(deltaFraction) {
    return `${roundMetric(Math.abs(deltaFraction) * 100)}%`;
}

function isAllocationRow(record) {
    const sku = String(record?.supplySku ?? "").trim();
    return sku && sku !== "-";
}

function sumEligibleFinalPercent(rows) {
    return (rows || []).reduce((sum, record) => {
        if (!isAllocationRow(record)) return sum;
        return sum + Number(record.eligibleFinalPercent || 0);
    }, 0);
}

/** Prefer server full-catalog SUMIF (`subcategoryEligibleTotalPercent`); fall back to visible rows. */
function resolveSubcategoryEligibleTotal(allocationRows) {
    for (const record of allocationRows) {
        const total = Number(record.subcategoryEligibleTotalPercent);
        if (Number.isFinite(total) && total > 0) {
            return total;
        }
    }
    return sumEligibleFinalPercent(allocationRows);
}

function hasForecastableAllocationRow(rows) {
    return (rows || []).some((record) => isAllocationRow(record) && record.forecastable);
}

function subcategoryEligibleTotalIsAtTarget(rawEligibleSum) {
    const rounded = roundPercentFraction(rawEligibleSum);
    return rounded >= SUBCATEGORY_TOTAL_TARGET - PERCENT_TOLERANCE;
}

function pickNormalizeRecipient(candidates) {
    if (!candidates.length) return null;
    const withExplicitOverride = candidates.filter(
        (record) =>
            record.overridePercent != null && Number.isFinite(Number(record.overridePercent))
    );
    const pool = withExplicitOverride.length ? withExplicitOverride : candidates;
    const lowest = [...pool].sort(
        (left, right) =>
            Number(left.eligibleFinalPercent || 0) - Number(right.eligibleFinalPercent || 0)
    )[0];
    if (lowest) return lowest;
    return [...candidates].sort(
        (left, right) =>
            Number(right.eligibleFinalPercent || 0) - Number(left.eligibleFinalPercent || 0)
    )[0];
}

function normalizeCandidatesForGroup(group) {
    return group.filter(
        (record) =>
            isAllocationRow(record) &&
            record.forecastable &&
            record.status !== "Inactive" &&
            record.status !== "Sunset"
    );
}

function isHealthyVarianceFlag(flag) {
    return String(flag || "").trim().toUpperCase() === "OK";
}

function isVarianceRebalanceDonor(record) {
    return (
        Number(record.variancePercent || 0) < -0.06 && !isHealthyVarianceFlag(record.varianceFlag)
    );
}

function isInventoryMixRebalanceDonor(record) {
    return Boolean(record.overstockFlag) && isHealthyVarianceFlag(record.varianceFlag);
}

export function generateRebalanceRecommendations(records) {
    const groups = new Map();

    records.forEach((record) => {
        const subCategory = record.subCategory || "Unassigned";
        if (!groups.has(subCategory)) {
            groups.set(subCategory, []);
        }
        groups.get(subCategory).push(record);
    });

    const normalizeRecommendations = [];
    const transferRecommendations = [];
    const usedSourceSkuIds = new Set();
    let subcategoryTransferCount = 0;

    const pushTransferRecommendation = (
        donor,
        recipient,
        subCategory,
        preferredDelta,
        reasonKind = "variance"
    ) => {
        if (usedSourceSkuIds.has(String(donor.id)) || String(donor.id) === String(recipient.id)) {
            return;
        }

        const maxTransfer = Math.max(Number(donor.adjustedFinalPercent || 0) - 0.005, 0);
        const suggestedDelta =
            preferredDelta ??
            Math.min(0.03, Math.max(0.01, Math.abs(Number(donor.variancePercent || 0)) / 4));
        const recommendedDelta = roundMetric(Math.min(suggestedDelta, maxTransfer));

        if (recommendedDelta < 0.01) {
            return;
        }

        const moveLabel = formatMovePercent(recommendedDelta);
        let governanceReason;
        if (reasonKind === "overstock") {
            governanceReason = `Structural underperformance and excess coverage in ${subCategory} justify moving ${moveLabel} of adjusted share from ${donor.supplySku} to ${recipient.supplySku} while preserving the subcategory 100% allocation total.`;
        } else if (reasonKind === "inventory-mix") {
            governanceReason = `Inventory mix rebalancing in ${subCategory}: ${donor.supplySku} is carrying excess coverage relative to demand, so shifting ${moveLabel} of adjusted share toward in-stock peer ${recipient.supplySku} aligns allocation with healthier inventory while preserving the governed 100% subcategory total.`;
        } else {
            governanceReason = `Persistent negative variance in ${subCategory} suggests rebalancing ${moveLabel} of adjusted share from ${donor.supplySku} toward the strongest eligible peer ${recipient.supplySku} without breaking the governed 100% subcategory constraint.`;
        }

        usedSourceSkuIds.add(String(donor.id));
        subcategoryTransferCount += 1;
        transferRecommendations.push({
            id: `${donor.id}-to-${recipient.id}`,
            kind: "transfer",
            subCategory,
            sourceSkuId: String(donor.id),
            sourceSku: donor.supplySku,
            sourceProductName: donor.productName,
            offsetSkuId: String(recipient.id),
            offsetSku: recipient.supplySku,
            offsetProductName: recipient.productName,
            rebalanceDriver: reasonKind,
            varianceFlag: donor.varianceFlag,
            variancePercent: donor.variancePercent,
            currentAdjustedFinalPercent: donor.adjustedFinalPercent,
            recommendedAdjustedFinalPercent: roundMetric(
                Number(donor.adjustedFinalPercent || 0) - recommendedDelta
            ),
            recipientAdjustedFinalPercent: recipient.adjustedFinalPercent,
            recipientRecommendedFinalPercent: roundMetric(
                Number(recipient.adjustedFinalPercent || 0) + recommendedDelta
            ),
            recommendedDelta: -recommendedDelta,
            governanceReason,
            recommendationStatus: "Pending",
        });
    };

    const pushNormalizeTo100Recommendation = (recipient, subCategory, gapFraction, rawEligibleSum) => {
        if (usedSourceSkuIds.has(String(recipient.id))) {
            return;
        }

        const gap = roundPercentFraction(gapFraction);
        if (gap <= PERCENT_TOLERANCE) {
            return;
        }

        const currentEligible = roundPercentFraction(Number(recipient.eligibleFinalPercent || 0));
        const nextEligible = roundPercentFraction(currentEligible + gap);
        const currentAdjusted = roundPercentFraction(Number(recipient.adjustedFinalPercent || 0));
        const nextAdjusted = roundPercentFraction(
            nextEligible / SUBCATEGORY_TOTAL_TARGET
        );
        const adjustedDelta = roundPercentFraction(nextAdjusted - currentAdjusted);

        usedSourceSkuIds.add(String(recipient.id));
        normalizeRecommendations.push({
            id: `${recipient.id}-normalize-100`,
            kind: "normalizeTo100",
            subCategory,
            sourceSkuId: String(recipient.id),
            sourceSku: recipient.supplySku,
            sourceProductName: recipient.productName,
            offsetSkuId: String(recipient.id),
            offsetSku: recipient.supplySku,
            offsetProductName: recipient.productName,
            varianceFlag: recipient.varianceFlag,
            variancePercent: recipient.variancePercent,
            currentAdjustedFinalPercent: currentAdjusted,
            recommendedAdjustedFinalPercent: nextAdjusted,
            nextEligibleOverridePercent: nextEligible,
            recommendedDelta: adjustedDelta,
            governanceReason: `Eligible overrides in ${subCategory} currently total ${roundMetric(
                rawEligibleSum * 100
            )}%. Increase ${recipient.supplySku}'s eligible override by ${formatMovePercent(
                gap
            )} to reach the governed 100% subcategory allocation.`,
            recommendationStatus: "Pending",
        });
    };

    for (const [subCategory, group] of Array.from(groups.entries())) {
        subcategoryTransferCount = 0;
        const allocationRows = group.filter(isAllocationRow);

        if (!hasForecastableAllocationRow(allocationRows)) {
            continue;
        }

        const rawEligibleSum = resolveSubcategoryEligibleTotal(allocationRows);
        const subcategoryAt100 = subcategoryEligibleTotalIsAtTarget(rawEligibleSum);

        const eligibleRecords = group.filter(
            (record) =>
                record.forecastable &&
                record.orderEligible &&
                record.status !== "Inactive" &&
                record.status !== "Sunset" &&
                Number(record.adjustedFinalPercent || 0) > 0
        );

        const normalizeCandidates = normalizeCandidatesForGroup(group);

        const recipients = [...eligibleRecords].sort((left, right) => {
            const leftScore =
                Number(left.variancePercent || 0) +
                Number(left.adjustedFinalPercent || 0) -
                (left.overstockFlag ? 0.08 : 0);
            const rightScore =
                Number(right.variancePercent || 0) +
                Number(right.adjustedFinalPercent || 0) -
                (right.overstockFlag ? 0.08 : 0);
            return rightScore - leftScore;
        });

        if (!subcategoryAt100) {
            const gapTo100 = roundPercentFraction(SUBCATEGORY_TOTAL_TARGET - rawEligibleSum);
            const normalizeRecipient = pickNormalizeRecipient(normalizeCandidates);
            if (gapTo100 > PERCENT_TOLERANCE && normalizeRecipient) {
                pushNormalizeTo100Recommendation(
                    normalizeRecipient,
                    subCategory,
                    gapTo100,
                    rawEligibleSum
                );
            }
            continue;
        }

        const strictDonors = eligibleRecords
            .filter(isVarianceRebalanceDonor)
            .sort(
                (left, right) =>
                    Math.abs(Number(right.variancePercent || 0)) -
                    Math.abs(Number(left.variancePercent || 0))
            );

        for (const donor of strictDonors) {
            const recipient = recipients.find(
                (candidate) =>
                    String(candidate.id) !== String(donor.id) &&
                    Number(candidate.adjustedFinalPercent || 0) > 0
            );
            if (!recipient) {
                continue;
            }

            pushTransferRecommendation(
                donor,
                recipient,
                subCategory,
                undefined,
                donor.overstockFlag ? "overstock" : "variance"
            );
        }

        if (subcategoryTransferCount < 2) {
            const inventoryMixDonors = eligibleRecords
                .filter(
                    (record) =>
                        isInventoryMixRebalanceDonor(record) &&
                        Number(record.adjustedFinalPercent || 0) > 0.01
                )
                .sort(
                    (left, right) =>
                        Number(left.adjustedFinalPercent || 0) -
                        Number(right.adjustedFinalPercent || 0)
                );

            for (const donor of inventoryMixDonors) {
                if (subcategoryTransferCount >= 2) {
                    break;
                }

                const recipient = recipients.find(
                    (candidate) =>
                        String(candidate.id) !== String(donor.id) &&
                        Number(candidate.adjustedFinalPercent || 0) >=
                            Number(donor.adjustedFinalPercent || 0)
                );
                if (!recipient) {
                    continue;
                }

                const inventoryDelta = Math.min(
                    0.02,
                    Math.max(0.01, Number(donor.adjustedFinalPercent || 0) * 0.3)
                );
                pushTransferRecommendation(
                    donor,
                    recipient,
                    subCategory,
                    inventoryDelta,
                    "inventory-mix"
                );
            }
        }
    }

    return [...normalizeRecommendations, ...transferRecommendations];
}

export function buildSkuRebalancePlanningRecords(
    allocationItems,
    forecastVsItems,
    subcategoryEligibleTotalsMap
) {
    const derived = deriveSkuAllocationChecks(allocationItems || [], {
        subcategoryEligibleTotalsMap,
    });
    const forecastById = new Map(
        (forecastVsItems || []).map((row) => [String(row.id), row])
    );

    return derived.map((row) => {
        const forecastRow = forecastById.get(String(row.id)) || {};
        const statusStr = String(row.status || "");
        const forecastable = Boolean(row.forecastable);

        return {
            ...row,
            variancePercent: Number(forecastRow.variancePercent ?? 0),
            varianceFlag: forecastRow.varianceFlag ?? "OK",
            overstockFlag: Boolean(forecastRow.overstockFlag),
            orderEligible:
                forecastable && statusStr !== "Sunset" && statusStr !== "Inactive",
            recommendedDelta: 0,
            recommendationKind: "none",
            offsetSku: "",
            governanceReason: forecastRow.governance ?? "",
            recommendationStatus: "None",
        };
    });
}

export function attachRebalanceRecommendations(records) {
    const recommendationLookup = new Map(
        generateRebalanceRecommendations(records).map((recommendation) => [
            recommendation.sourceSkuId,
            recommendation,
        ])
    );

    return records.map((record) => {
        const recommendation = recommendationLookup.get(String(record.id));
        if (!recommendation) {
            return record;
        }

        return {
            ...record,
            recommendedDelta: recommendation.recommendedDelta,
            recommendationKind: recommendation.kind || "transfer",
            rebalanceDriver: recommendation.rebalanceDriver || recommendation.kind || "transfer",
            offsetSku: recommendation.offsetSku,
            governanceReason: recommendation.governanceReason,
            recommendationStatus: recommendation.recommendationStatus,
            nextEligibleOverridePercent: recommendation.nextEligibleOverridePercent,
            recommendedAdjustedFinalPercent: recommendation.recommendedAdjustedFinalPercent,
        };
    });
}

export function buildPendingSkuRebalanceGroups(tableRecords, processedRecommendationIds) {
    const grouped = new Map();

    (tableRecords || [])
        .filter(
            (record) =>
                Number(record.recommendedDelta || 0) !== 0 &&
                record.recommendationStatus === "Pending" &&
                !processedRecommendationIds.has(String(record.id))
        )
        .forEach((record) => {
            const subCategory = record.subCategory || "Unassigned";
            if (!grouped.has(subCategory)) {
                grouped.set(subCategory, []);
            }
            grouped.get(subCategory).push(record);
        });

    return Array.from(grouped.entries())
        .map(([subCategory, rows]) => ({
            subCategory,
            rows: rows.sort(
                (left, right) =>
                    Math.abs(Number(right.recommendedDelta || 0)) -
                    Math.abs(Number(left.recommendedDelta || 0))
            ),
        }))
        .sort((left, right) => right.rows.length - left.rows.length);
}
